/**
 * 日期跳转选择器组件
 * 
 * 提供快捷跳转按钮和日历选择器，用于在甘特图中快速导航到指定日期
 * 
 * Requirements: 11.1, 11.2, 11.3
 */

import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface DateJumpPickerProps {
  /** 当前显示的日期 */
  currentDate: Date
  /** 项目开始日期 */
  projectStartDate?: Date
  /** 项目结束日期 */
  projectEndDate?: Date
  /** 日期选择回调 */
  onDateSelect: (date: Date) => void
  /** 自定义类名 */
  className?: string
}

/**
 * 快捷跳转选项
 */
export type QuickJumpOption = 'today' | 'week_start' | 'month_start' | 'project_start' | 'project_end'

/**
 * 获取快捷跳转日期
 */
export function getQuickJumpDate(
  option: QuickJumpOption,
  projectStartDate?: Date,
  projectEndDate?: Date
): Date {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  switch (option) {
    case 'today':
      return today

    case 'week_start': {
      const weekStart = new Date(today)
      const day = weekStart.getDay()
      // 周一为一周开始
      const diff = day === 0 ? -6 : 1 - day
      weekStart.setDate(weekStart.getDate() + diff)
      return weekStart
    }

    case 'month_start': {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
      return monthStart
    }

    case 'project_start':
      return projectStartDate ? new Date(projectStartDate) : today

    case 'project_end':
      return projectEndDate ? new Date(projectEndDate) : today

    default:
      return today
  }
}

/**
 * 日期跳转选择器组件
 * 
 * Requirements: 11.1, 11.2, 11.3
 */
export function DateJumpPicker({
  currentDate,
  projectStartDate,
  projectEndDate,
  onDateSelect,
  className,
}: DateJumpPickerProps) {
  const [open, setOpen] = useState(false)
  const [calendarDate, setCalendarDate] = useState<Date | undefined>(currentDate)

  const handleQuickJump = (option: QuickJumpOption) => {
    const date = getQuickJumpDate(option, projectStartDate, projectEndDate)
    onDateSelect(date)
    setOpen(false)
  }

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      setCalendarDate(date)
      onDateSelect(date)
      setOpen(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5', className)}
        >
          <CalendarIcon className="h-4 w-4" />
          跳转
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* 快捷跳转按钮 (Requirement 11.2) */}
          <div className="border-r p-2 space-y-1">
            <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
              快捷跳转
            </p>
            <QuickJumpButton
              label="今天"
              onClick={() => handleQuickJump('today')}
            />
            <QuickJumpButton
              label="本周开始"
              onClick={() => handleQuickJump('week_start')}
            />
            <QuickJumpButton
              label="本月开始"
              onClick={() => handleQuickJump('month_start')}
            />
            {projectStartDate && (
              <QuickJumpButton
                label="项目开始"
                onClick={() => handleQuickJump('project_start')}
              />
            )}
            {projectEndDate && (
              <QuickJumpButton
                label="项目结束"
                onClick={() => handleQuickJump('project_end')}
              />
            )}
          </div>

          {/* 日历选择器 (Requirement 11.1) */}
          <div className="p-2">
            <Calendar
              mode="single"
              selected={calendarDate}
              onSelect={handleCalendarSelect}
              initialFocus
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * 快捷跳转按钮
 */
interface QuickJumpButtonProps {
  label: string
  onClick: () => void
}

function QuickJumpButton({ label, onClick }: QuickJumpButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-md px-3 py-1.5 text-left text-sm hover:bg-accent transition-colors"
    >
      {label}
    </button>
  )
}

/**
 * 格式化日期为显示字符串
 */
export function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

/**
 * 验证快捷跳转日期计算
 * 用于属性测试
 */
export function validateQuickJumpDate(
  option: QuickJumpOption,
  projectStartDate?: Date,
  projectEndDate?: Date
): {
  date: Date
  isValid: boolean
  description: string
} {
  const date = getQuickJumpDate(option, projectStartDate, projectEndDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  let isValid = true
  let description = ''

  switch (option) {
    case 'today':
      isValid = date.getTime() === today.getTime()
      description = '应该返回今天的日期'
      break

    case 'week_start': {
      const dayOfWeek = date.getDay()
      isValid = dayOfWeek === 1 // 周一
      description = '应该返回本周一的日期'
      break
    }

    case 'month_start':
      isValid = date.getDate() === 1
      description = '应该返回本月1号'
      break

    case 'project_start':
      if (projectStartDate) {
        isValid = date.getTime() === new Date(projectStartDate).setHours(0, 0, 0, 0)
        description = '应该返回项目开始日期'
      } else {
        isValid = date.getTime() === today.getTime()
        description = '无项目开始日期时应返回今天'
      }
      break

    case 'project_end':
      if (projectEndDate) {
        isValid = date.getTime() === new Date(projectEndDate).setHours(0, 0, 0, 0)
        description = '应该返回项目结束日期'
      } else {
        isValid = date.getTime() === today.getTime()
        description = '无项目结束日期时应返回今天'
      }
      break
  }

  return { date, isValid, description }
}
